import { useMemo } from "react";
import { MetricsEnergyEnum } from "../pages/Projects/MetricsData";
import { useProjectEnergyMetricsQuery } from "../services/query/project-asset-details.query";
import useAppConfigStore from "../store/useAppConfigStore";
import { useIsRefreshedDashboardFlag } from "./useAppConfigFlags";
/**
 * Owns the energy-metrics request plus the mapping the cards need, so both the Live and
 * Historic dashboards can consume it directly. React Query dedupes by key, so mounting it
 * from more than one component issues a single request.
 */
export const useEnergyMetricsCards = (projectId, startDate, refreshTimestamp) => {
    const { data: { energyMetrics = [], productivityMetrics = [] } = {}, refetch: refetchProjectEnergyMetrics, status: energyMetricsApiStatus } = useProjectEnergyMetricsQuery(projectId, startDate, refreshTimestamp);
    const { appConfig } = useAppConfigStore((state) => ({
        appConfig: state.appConfig
    }));
    const showProductivity = appConfig?.release_1_1?.showProductivity;
    const isLiveDashboardRefreshEnabled = useIsRefreshedDashboardFlag();
    const energyMetricsData = useMemo(() => {
        // Build a keyed map first so display order is derived from MetricsEnergyEnum once at
        // the end, rather than from sparse index assignment that silently breaks when enum
        // keys are reordered or when earlier metrics are absent from the API response.
        const metricsByKey = new Map();
        const energyMetricNames = energyMetrics?.[0] && Object.keys(energyMetrics[0]);
        energyMetricNames &&
            energyMetricNames.forEach((metricName) => {
                const metric = energyMetrics[0][metricName];
                const metricTitle = MetricsEnergyEnum[metricName];
                if (metric && metricTitle) {
                    metricsByKey.set(metricName, {
                        currentDay: metric?.currentDay,
                        metric,
                        name: metricTitle
                    });
                }
            });
        // The refreshed Live tab always shows a Productivity card, independent of showProductivity.
        if (showProductivity || isLiveDashboardRefreshEnabled) {
            const mapProductivity = (prod, enumKey) => {
                if (!prod)
                    return;
                metricsByKey.set(enumKey, {
                    currentDay: prod.currentDay ?? null,
                    metric: {
                        lastDay: {
                            energyTrend: prod.lastDay?.productivityTrend ?? null,
                            previousValue: prod.lastDay?.previousValue ?? null,
                            value: prod.lastDay?.value ?? null
                        },
                        lastMonth: {
                            energyTrend: prod.lastMonth?.productivityTrend ?? null,
                            value: prod.lastMonth?.value ?? null
                        },
                        lastWeek: {
                            energyTrend: prod.lastWeek?.productivityTrend ?? null,
                            value: prod.lastWeek?.value ?? null
                        }
                    },
                    name: MetricsEnergyEnum[enumKey]
                });
            };
            // The contract allows the two series to arrive as separate array entries (maxItems: 2).
            mapProductivity(productivityMetrics?.find((entry) => entry?.haulerProductivity)?.haulerProductivity, "haulerDailyPrediction");
            mapProductivity(productivityMetrics?.find((entry) => entry?.loaderProductivity)?.loaderProductivity, "loadOutDailyProduction");
            // Ensure both cards are always present (fall back to null if not in API response)
            const productivityEnumKeys = ["haulerDailyPrediction", "loadOutDailyProduction"];
            productivityEnumKeys.forEach((key) => {
                if (!metricsByKey.has(key)) {
                    metricsByKey.set(key, {
                        currentDay: null,
                        metric: {
                            currentDay: null,
                            lastDay: { energyTrend: null, value: null },
                            lastMonth: { energyTrend: null, value: null },
                            lastWeek: { energyTrend: null, value: null }
                        },
                        name: MetricsEnergyEnum[key]
                    });
                }
            });
        }
        // Reconstruct the ordered array using enum declaration order as the single source of
        // truth. Only present keys are included — no sparse holes.
        return Object.keys(MetricsEnergyEnum)
            .filter((key) => metricsByKey.has(key))
            .map((key) => metricsByKey.get(key));
    }, [energyMetrics, productivityMetrics, showProductivity, isLiveDashboardRefreshEnabled]);
    return { energyMetricsApiStatus, energyMetricsData, refetchProjectEnergyMetrics };
};
